import React from 'react';
import CriteriaList from '../../components/CriteriaList';
import ENDPOINT from '../../constants/endpoints';
import ERROR_CODE from '../../constants/error-codes';
import ROUTE from '../../constants/route';
import {UPGRADE_ACCOUNT_TYPE} from '../../constants/upgrade-account-types';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import ConfirmDialog from '../../scenes/Dialog/components/ConfirmDialog';
import ExtendedConfirmDialog from '../../scenes/Dialog/components/ExtendedConfirmDialog'
import BasicButtonGroup from '../../components/BasicButtonGroup'
import BasicButton from '../../components/BasicButton'
import FormDialog from '../../scenes/Dialog/components/FormDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import UpgradeRecruiterAccount from '../../scenes/Dialog/components/UpgradeRecruiterAccount';
import CoverLetterForm from '../../scenes/Form/components/CoverLetterForm';
import ProfileRequestRecommendationForm from '../../scenes/Form/components/ProfileRequestRecommendationForm';
import UploadResumeForm from '../../scenes/Form/components/UploadResumeForm';
import {Permission} from '../user';

export class JobActions {
  constructor(api, history, user) {
    this.api = api;
    this.user = user;
    this.history = history;
  }

  _sendVerifyRequest = process => {
    const { ProcessID: id, Type: type } = process;
    const endpoint =
      type === 'Job'
        ? ENDPOINT.ACTION.APPLY_JOB
        : ENDPOINT.ACTION.APPLY_PROJECT;

    return new Promise((resolve, reject) => {
      this.api.post(
        endpoint,
        { id, verify: 'Yes' },
        {
          handleErrorCode: ({ data: { code, filters = null } }) => {
            reject({ code, filters });
          },
          handleError: response => {
            resolve(false);
            ResponseErrorDialog({ response });
          },
          handleSuccess: () => resolve(true),
        }
      );
    });
  };

  _showUploadResumeDialog = updateUserResume => {
    return new Promise(resolve => {
      FormDialog({
        modeless: false,
        compact: false,
        popupStyles: {
          paddingBottom: '30px',
        },
        dismissWhenClose: false,
        params: {
          message:
            'You need to upload a resume before you can apply for this position.',
        },
        form: (actions, params) => (
          <UploadResumeForm actions={actions} params={params} />
        ),
      }).then(
        () => {
          updateUserResume();
          BasicDialog({
            dismissWhenClose: false,
            bodyRender: (
              <p className="dialog__message">
                Resume was successfully uploaded.
              </p>
            ),
          }).then(() => resolve(true), () => resolve(true));
        },
        () => resolve(false)
      );
    });
  };

  _showRequireRecomDialog = () => {
    ConfirmDialog({
      bodyRender: 'Require a recommendation to apply for this position.',
      textSuccessBtn: 'Add Recommendation',
    }).then(() =>
      FormDialog({
        title: 'Ask for Recommendation',
        compact: true,
        form: (actions, params) => (
          <ProfileRequestRecommendationForm actions={actions} params={params} />
        ),
      }).then(
        () =>
          BasicDialog({
            bodyRender:
              'Your recommendation request has been sent to the intended recipient.',
          }),
        response => ResponseErrorDialog({ response })
      )
    );
  };

  _showRequireGatDialog = () => {
    ConfirmDialog({
      bodyRender:
        'Please take the GoBuyside Aptitude Test to apply for this position.',
      textSuccessBtn: 'Go to GoBuyside Aptitude Test',
    }).then(
      // TODO change to history push
      () => (window.location = '/candidate/aptitude')
    );
  };

  _showProcessDeadlineDialog = () => {
    BasicDialog({
      bodyRender: (
        <div>
          The application deadline has passed and firm has begun evaluating
          candidates.
          <br />
          Send an InBound to express your interest mid-process.
        </div>
      ),
    });
  };

  _showRequireCriteriaDialog = filters => {
    if (
      Permission.isAllowedViewProcessApplyRequirementsMismatch(
        this.user.permissions
      )
    ) {
      BasicDialog({
        bodyRender: (
          <div>
            You are unable to apply for this position because the firm has
            specified the below criteria for this search{' '}
            <CriteriaList records={filters} />
          </div>
        ),
        popupStyles: { maxWidth: '850px' },
        buttonStyles: { width: 150 },
        dismissWhenClose: false,
      }).then(null, () => {});
    } else {
      if (UPGRADE_ACCOUNT_TYPE.RECRUITER === this.user.accountUpgradeType) {
        UpgradeRecruiterAccount({
          initialText:
            'You are unable to apply for this position because you do not match the criteria the firm specified for this search.\nUpgrade to premium to see Search Criteria.',
        });
      } else if (
        UPGRADE_ACCOUNT_TYPE.CANDIDATE === this.user.accountUpgradeType
      ) {
        ConfirmDialog({
          bodyRender: (
            <div>
              You are unable to apply for this position because you do not match
              the criteria the firm specified for this search.{' '}
              <div>Upgrade to premium to see Search Criteria.</div>
            </div>
          ),
          textSuccessBtn: 'Upgrade to premium',
        }).then(
          () =>
            this.history.push(ROUTE.CANDIDATE_UPGRADE_PREMIUM, {
              returnUrl: window.location.pathname,
            }),
          () => {}
        );
      }
    }
  };

  _requestApply = (id, type, letter) => {
    const endpoint =
      type === 'Job'
        ? ENDPOINT.ACTION.APPLY_JOB
        : ENDPOINT.ACTION.APPLY_PROJECT;

    return new Promise(resolve => {
      this.api.post(
        endpoint,
        { id, CoverLetterFile: letter },
        {
          handleError: response => {
            resolve(false);
            ResponseErrorDialog({ response });
          },
          handleSuccess: () => resolve(true),
        }
      );
    });
  };

  checkingApply = (process, updateUserResume) => {
    return new Promise(resolve => {
      this._sendVerifyRequest(process)
        .then(success => resolve(success))
        .catch(({ code, filters }) => {
          if (ERROR_CODE.NO_RESUME === code) {
            this._showUploadResumeDialog(updateUserResume).then(success =>
              success
                ? this.checkingApply(process).then(success => resolve(success))
                : resolve(false)
            );
          } else {
            if (ERROR_CODE.DEADLINE === code) {
              this._showProcessDeadlineDialog(resolve);
            } else if (ERROR_CODE.NO_RECOM === code) {
              this._showRequireRecomDialog(resolve);
            } else if (ERROR_CODE.NO_GAT === code) {
              this._showRequireGatDialog(resolve);
            } else if (ERROR_CODE.CRITERIA_MISMATCH === code) {
              this._showRequireCriteriaDialog(filters);
            }
            resolve(false);
          }
        });
    });
  };

  apply = process => {
    const {
      CoverLetterRequire,
      AdditionalOptions = {},
      ProcessID,
      Type,
      ATSLink,
    } = process;

    const sendAtsConfirmationRequest = (process, isProceed) => {
      const { ProcessID: id, Type: type } = process;
      const endpoint =
        type === 'Job'
          ? ENDPOINT.ACTION.APPLY_JOB_ATS_CONFIRMATION
          : ENDPOINT.ACTION.APPLY_PROJECT_ATS_CONFIRMATION;

      return new Promise((resolve, reject) => {
        this.api.post(
          endpoint,
          { id, isProceed: isProceed ? 'Yes' : 'No' },
          {
            handleErrorCode: ({ data: { code, filters = null } }) => {
              reject({ code, filters });
            },
            handleError: response => {
              resolve(false);
              ResponseErrorDialog({ response });
            },
            handleSuccess: () => resolve(true),
          }
        );
      });
    };

    return new Promise((resolve, reject) => {
      if (
        CoverLetterRequire === 'Yes' ||
        (AdditionalOptions && AdditionalOptions.coverLetterReq)
      ) {
        FormDialog({
          modeless: false,
          popupStyles: {
            paddingBottom: '30px',
          },
          dismissWhenClose: false,
          form: (actions, params) => (
            <CoverLetterForm actions={actions} params={params} />
          ),
        }).then(letter => {
          resolve(letter);
        }, reject);
      } else {
        resolve();
      }
    })
      .then(letter => this._requestApply(ProcessID, Type, letter))
      .then(success => {
        if (success && ATSLink) {
          const processATS = () => {
            if (success && ATSLink) {
              return sendAtsConfirmationRequest(process, ATS_Status).then(() => {
                if (ATS_Status) {
                  window.location = ATSLink;
                }
                return success;
              })
            }
            return success;
          }
          let ATS_Status = false;
          return ExtendedConfirmDialog({
            bodyRender: (
              <div>
                To complete your application, you must complete an application via a third party applicant website.
              </div>
            ),
            buttonsRender: ({
                              proceed,
                              textSuccessBtn,
                              textCancelBtn,
                              btnGroupStyles
                            }) => (
              <BasicButtonGroup
                groupStyles={btnGroupStyles}
              >
                <BasicButton
                  controlFunc={() => {proceed(true)}}
                  btnWidth={200}
                  btnHeight={40}
                  btnFontWeight={500}
                  btnArrow={false}
                  btnTextColor="#27282d"
                  btnText={textSuccessBtn}
                />
                <BasicButton
                  controlFunc={() => {proceed(false)}}
                  btnWidth={200}
                  btnHeight={40}
                  btnFontWeight={500}
                  btnArrow={false}
                  btnTextColor="#27282d"
                  btnText={textCancelBtn}
                />
              </BasicButtonGroup>
            ),
            popupStyles: { maxWidth: '850px' },
            dismissWhenClose: false,
            textSuccessBtn: 'Proceed',
            textCancelBtn: 'Cancel',
          }).then((status) => {
            ATS_Status = status;
            return processATS();
          }).catch(() => {
            return processATS();
          })
        }
        return success;
      })
      .catch(() => false);
  };
}
